import { useQuery } from '@tanstack/react-query';
import type { Block, Unit } from '@azko/shared';
import { api } from './api.js';

/**
 * A lista de blocos do condominio, para os seletores.
 *
 * Nasceu de um bug com um sintoma so: o campo "Bloco" nao aparecia em
 * condominio nenhum que estivesse comecando. A primeira versao deduzia os
 * blocos das UNIDADES ja carregadas — o que parecia esperto (o dado ja estava
 * na tela, sem requisicao a mais) e era errado pela ordem em que o predio se
 * monta: primeiro se cria a Torre A, depois os apartamentos dela. Entre uma
 * coisa e outra o bloco existe e nenhuma unidade aponta para ele, entao a
 * deducao devolvia lista vazia e o campo sumia — justo no onboarding, que e a
 * unica hora em que ele importa de verdade.
 *
 * Agora a fonte e `GET /blocks`. Ela pede `condominio:unidades:visualizar`,
 * que o porteiro tambem tem (herda do `viewer`), entao nao ha papel que use
 * estas telas e leve 403 aqui.
 *
 * A deducao ficou como REDE: se a rota falhar — tipicamente 403 num banco cuja
 * matriz de permissoes esta desatualizada —, o seletor volta a mostrar os
 * blocos que aparecem nas unidades, em vez de sumir da tela.
 */
export function useBlocks(units: Unit[]): Array<[id: string, name: string]> {
  const { data } = useQuery({
    queryKey: ['blocks'],
    queryFn: () => api.get<{ items: Block[] }>('/blocks'),
    staleTime: 5 * 60_000,
    // Sem repetir: 403 nao melhora na segunda tentativa, e o fallback ja cobre.
    retry: false,
  });

  const fromApi = (data?.items ?? []).map(
    (block) => [block.id, block.name] as [string, string],
  );

  // Enquanto a resposta nao chega — ou se ela falhou — vale o que as unidades
  // souberem. Condicionar so ao erro fazia o campo piscar em toda montagem:
  // sumia no primeiro render e aparecia quando a query voltava.
  if (fromApi.length > 0) return fromApi;

  return [
    ...new Map(
      units.filter((unit) => unit.blockId).map((unit) => [unit.blockId as string, unit.block]),
    ),
  ].sort((a, b) => a[1].localeCompare(b[1], 'pt-BR', { numeric: true }));
}
